const ORDERS_KEY = '__yaychat.recharge_orders.v1'
const ACTIVE_KEY = '__yaychat.active_recharge_result.v1'
const MAX_AGE = 24 * 60 * 60 * 1000
const pendingVerifications = new Map()

function read(storage, key, fallback) {
  try { return JSON.parse(storage?.getItem(key)) ?? fallback } catch { return fallback }
}

function write(storage, key, value) {
  try { storage?.setItem(key, JSON.stringify(value)) } catch { /* 禁用存储时仍可验单。 */ }
}

function scalar(value) {
  return `${(Array.isArray(value) ? value[0] : value) ?? ''}`
}

export function isRechargeCallback(query) {
  return Boolean(scalar(query.session_id)) || ['gem_result', 'payermax', 'paypal', 'diandianpay', 'airwallex', 'pingpong']
    .some(key => scalar(query[key]) === '1')
}

export function createRechargeResultStore(session, persistent, now = Date.now) {
  function orders() {
    const records = { ...read(persistent, ORDERS_KEY, {}) }
    for (const [key, record] of Object.entries(read(session, ORDERS_KEY, {}))) {
      if (!records[key] || record.updatedAt > records[key].updatedAt) records[key] = record
    }
    return Object.fromEntries(Object.entries(records).filter(([, record]) => (
      record?.orderId && record?.userId && now() - record.createdAt < MAX_AGE
    )))
  }

  function save(record) {
    if (!record?.orderId || !record?.userId) return
    const records = orders()
    const key = `${record.userId}:${record.orderId}`
    records[key] = { ...records[key], ...record, createdAt: records[key]?.createdAt ?? now(), updatedAt: now() }
    // 按订单保存，不再用一份“最近充值”覆盖所有订单。保留最近 30 笔，自动过期。
    const recent = Object.fromEntries(Object.entries(records)
      .sort((a, b) => b[1].createdAt - a[1].createdAt).slice(0, 30))
    write(persistent, ORDERS_KEY, recent)
    write(session, ORDERS_KEY, recent)
  }

  function find(query) {
    const uid = scalar(query.uid)
    const orderId = scalar(query.orderId ?? query.order_id ?? query.yay_oid)
    const sessionId = scalar(query.session_id)
    if (!orderId && !sessionId) return null
    const matches = Object.values(orders()).filter(record => (
      (!uid || `${record.userId}` === uid)
      && (orderId
        ? [record.orderId, record.paymentId, record.paypalOrderId].map(scalar).includes(orderId)
        : record.stripeSessionId === sessionId)
    ))
    return matches.length === 1 ? matches[0] : null
  }

  return {
    save,
    find,
    get: (uid, orderId) => orders()[`${uid}:${orderId}`] ?? null,
    setActive: result => write(session, ACTIVE_KEY, { ...result, ...rechargeDisplayResult(result), savedAt: now() }),
    getActive(uid) {
      const result = read(session, ACTIVE_KEY, null)
      return result && `${result.userId}` === `${uid}` && now() - result.savedAt < MAX_AGE
        ? { ...result, ...rechargeDisplayResult(result) } : null
    },
    clearActive() { try { session?.removeItem(ACTIVE_KEY) } catch { /* 无可用存储。 */ } },
  }
}

export function rechargeReceiptRequest(query, context) {
  const uid = scalar(context?.userId || query.uid)
  const orderId = scalar(context?.orderId || query.orderId || query.order_id || query.yay_oid)
  if (!/^\d+$/.test(uid) || !orderId) return null
  let extra = { ...query }
  delete extra.gem_result
  if (context?.rechargeType !== 'gems') {
    if (query.session_id) extra = { session_id: scalar(query.session_id), packageName: 'yay.chat.app' }
    else if (scalar(query.payermax) === '1') extra = { outTradeNo: query.outTradeNo, packageName: 'yay.chat.app' }
    else if (scalar(query.paypal) === '1') extra = { paypalOrderId: query.token, packageName: 'yay.chat.app' }
    else if (scalar(query.diandianpay) === '1') extra = { diandianId: query.ddid, packageName: 'yay.chat.app' }
    else if (scalar(query.airwallex) === '1') extra = { id: query.id, packageName: 'yay.chat.app' }
    else {
      delete extra.uid
      delete extra.orderId
      delete extra.order_id
    }
  }
  return { order_id: orderId, user_id: uid, extra_json: JSON.stringify(extra) }
}

export function receiptResult(response, rechargeType = 'coins', orderId = '') {
  const data = response?.data
  const items = Array.isArray(data?.item) ? data.item : data?.item ? [data.item] : []
  const item = items.find(item => `${item.orderId ?? item.order_id}` === `${orderId}`)
    || (items.length === 1 && !items[0].orderId && !items[0].order_id ? items[0] : null)
  if (items.length && !item) return { status: 'processing', message: '' }
  const payStatus = `${item?.payStatus ?? item?.pay_status ?? data?.payStatus ?? data?.pay_status ?? ''}`.toUpperCase()
  if (payStatus === 'SUCCESS') return { status: 'success', message: '' }
  if (['PAYING', 'PROCESSING', 'PENDING', 'CREATED'].includes(payStatus)) return { status: 'processing', message: '' }
  if (['FAILED', 'FAIL', 'CANCELLED', 'CANCELED', 'CLOSED', 'EXPIRED'].includes(payStatus)) {
    return { status: 'failed', message: response?.message || '' }
  }
  // 验单频控/重复验单不是支付失败；只有服务端确认成功才能显示成功。
  if (`${response?.code}` === '1001510117' || /verify too many|too many requests|rate.?limit|already verif/i.test(response?.message || '')) {
    return { status: 'processing', message: response?.message || '' }
  }
  // 接口调用成功不等于到账成功；缺少明确支付成功状态也属于未确认。
  return { status: 'processing', message: '' }
}

export function rechargeDisplayResult(result) {
  // 页面只展示成功/失败。内部未确认状态仍供短时去重使用，
  // 不把展示失败永久缓存为订单终态，后续真实成功回调仍可重新验单。
  if (result?.status === 'success' || result?.status === 'failed') return result
  return { status: 'failed', message: result?.message || '' }
}

export async function verifyRechargeOnce(store, request, verify, rechargeType, now = Date.now) {
  const { user_id: uid, order_id: orderId } = request
  const key = `${uid}:${orderId}`
  let callback = {}
  try { callback = JSON.parse(request.extra_json || '{}') || {} } catch { /* 兼容旧订单。 */ }
  const signal = JSON.stringify(['pingpong', 'status', 'transactionId', 'merchantTransactionId', 'session_id', 'outTradeNo', 'paypalOrderId']
    .map(field => scalar(callback[field])))
  const saved = store.get(uid, orderId)
  // 已确认终态可复用；处理中只复用相同回跳信号，避免旧 PAYING 挡住新 SUCCESS 回调。
  if (saved?.result && (saved.result.status !== 'processing'
    || (saved.verificationSignal === signal && now() - saved.checkedAt < 10000))) {
    return saved.result
  }
  const pending = pendingVerifications.get(key)
  if (pending) {
    const result = await pending.promise
    if (pending.signal === signal || result.status !== 'processing') return result
    return verifyRechargeOnce(store, request, verify, rechargeType, now)
  }

  const verification = (async () => {
    let result
    try { result = receiptResult(await verify(request), rechargeType, orderId) }
    catch { result = { status: 'processing', message: '' } }
    store.save({ ...saved, orderId, userId: uid, rechargeType, result, checkedAt: now(), verificationSignal: signal })
    return result
  })()
  pendingVerifications.set(key, { promise: verification, signal })
  try { return await verification } finally { pendingVerifications.delete(key) }
}
